/**
 * VideoGenModal - Fixed Version
 * ✅ Working video generation (fixed Firestore bugs)
 * ✅ Full Edit panel with text overlays, logos, colors, shadows
 * ✅ All advanced editing features restored
 * ✅ useReducer refactor for edit state
 * ✅ All variable references fixed
 * ✅ Correct model pricing (11 models)
 * ✅ Fixed aspect ratio labels (Portrait → Mobile)
 * ✅ Fixed Landscape ratio (9:16 → 16:9)
 * ✅ Dynamic preview window resizing
 */

import React, { useState, useCallback, useEffect, useRef, useReducer, useMemo } from 'react';
import { 
    X, Play, Download, Edit2, Save, Share2, Upload, 
    Sparkles, ChevronDown, UploadCloud, Type, Image as ImageIcon 
} from 'lucide-react';
import apiService from '../../services/apiService';

// Constants
const FREE_DAILY_LIMIT = 3;
const FREE_MONTHLY_LIMIT = 15;

// --- Reusable Sub-components ---

const EditSlider = ({ label, value, unit, ...props }) => (
    <div>
        <label
            className="vault-modal-sub"
            style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}
        >
            {label}: {value}{unit}
        </label>
        <input
            type="range"
            value={value}
            {...props}
            className="vault-range"
        />
    </div>
);

// --- END Sub-components ---

// --- CONSTANTS (Moved outside component) ---

// Available models - Updated with correct pricing and all 11 models
const videoModels = [
    // Free Model (daily/monthly allowance) — verified ImageRouter slug
    {
        id: 'bytedance/seedance-1-pro-fast',
        name: 'Seedance Fast (Free)',
        cost: 'Free',
        description: 'Fast video generation with daily limits',
        isFree: true
    },
    // Premium Models (sorted by cost; credits = ceil(provider_cost x 125))
    {
        id: 'minimax/hailuo-2.3-fast',
        name: 'Hailuo 2.3 Fast',
        cost: '24 credits',
        description: 'Fast Minimax video',
        isFree: false
    },
    {
        id: 'google/veo-3.1-lite',
        name: 'Veo 3.1 Lite',
        cost: '25 credits',
        description: 'Lightweight Google Veo 3.1',
        isFree: false
    },
    {
        id: 'lightricks/ltx-2-fast',
        name: 'LTX 2 Fast',
        cost: '30 credits',
        description: 'Fast Lightricks video',
        isFree: false
    },
    {
        id: 'bytedance/seedance-1.5-pro',
        name: 'Seedance 1.5 Pro',
        cost: '33 credits',
        description: 'ByteDance pro video',
        isFree: false
    },
    {
        id: 'kling/kling-2.5-turbo-pro',
        name: 'Kling 2.5 Turbo Pro',
        cost: '44 credits',
        description: 'Kling turbo, pro quality',
        isFree: false
    },
    {
        id: 'google/veo-3.1-fast',
        name: 'Veo 3.1 Fast',
        cost: '57 credits',
        description: 'Fast Google Veo 3.1',
        isFree: false
    },
    {
        id: 'kling/kling-3.0-turbo',
        name: 'Kling 3.0 Turbo',
        cost: '70 credits',
        description: 'Latest Kling turbo',
        isFree: false
    },
    {
        id: 'bytedance/seedance-2-fast',
        name: 'Seedance 2 Fast',
        cost: '82 credits',
        description: 'ByteDance Seedance 2, fast',
        isFree: false
    },
    {
        id: 'google/veo-3.1',
        name: 'Veo 3.1',
        cost: '150 credits',
        description: 'Highest quality Google Veo',
        isFree: false
    },
    {
        id: 'openai/sora-2-pro',
        name: 'Sora 2 Pro',
        cost: '150 credits',
        description: 'OpenAI Sora 2, pro quality',
        isFree: false
    },
    {
        id: 'kling/kling-3.0-4k',
        name: 'Kling 3.0 4K',
        cost: '263 credits',
        description: 'Ultra-HD 4K Kling video',
        isFree: false
    }
];

// Position classes for text/logo overlay
const positionClasses = {
    'top-left': 'top-4 left-4',
    'top-center': 'top-4 left-1/2 -translate-x-1/2',
    'top-right': 'top-4 right-4',
    'center-left': 'top-1/2 left-4 -translate-y-1/2',
    'center': 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2',
    'center-right': 'top-1/2 right-4 -translate-y-1/2',
    'bottom-left': 'bottom-4 left-4',
    'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2',
    'bottom-right': 'bottom-4 right-4'
};

// --- Reducer for Advanced Edit State ---
const editInitialState = {
    isEditMode: false,
    overlayText: '',
    textColor: '#FFFFFF',
    fontSize: 24,
    textPosition: 'bottom-center',
    fontFamily: 'Arial',
    isBold: false,
    isItalic: false,
    shadowColor: '#000000',
    shadowOpacity: 0.7,
    logoUrl: null,
    logoFile: null,
    logoSize: 15,
    logoPosition: 'top-right',
    logoOpacity: 0.8,
};

function editReducer(state, action) {
    switch (action.type) {
        case 'SET_EDIT_MODE':
            return { ...state, isEditMode: action.payload };
        case 'SET_FIELD':
            return { ...state, [action.field]: action.payload };
        case 'SET_LOGO':
            return { ...state, logoFile: action.payload.file, logoUrl: action.payload.url };
        case 'REMOVE_LOGO':
            return { ...state, logoFile: null, logoUrl: null };
        default:
            return state;
    }
}

// --- MAIN COMPONENT ---

const VideoGenModal = ({ isOpen, onClose, showNotification: showNotificationProp }) => {

    // ✅ Robust notification: Dashboard historically passed `onGenerate` instead of
    // `showNotification`, which silently no-op'd toasts. Accept the prop when present,
    // otherwise fall back to a safe no-op so notifications never throw.
    const showNotification = useMemo(
        () => (typeof showNotificationProp === 'function' ? showNotificationProp : () => {}),
        [showNotificationProp]
    );
    
    // --- Core State ---
    const [prompt, setPrompt] = useState('');
    const [selectedModel, setSelectedModel] = useState(videoModels[0]); // Default to free model
    const [aspectRatio, setAspectRatio] = useState('16:9'); // ✅ Added aspect ratio state
    const [duration, setDuration] = useState('5s'); // ✅ Added duration state
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatedVideoUrl, setGeneratedVideoUrl] = useState(null);
    const [recentGenerations, setRecentGenerations] = useState([]);
    const [isModelMenuOpen, setIsModelMenuOpen] = useState(false);
    
    // User credits and usage
    const [userCredits, setUserCredits] = useState(0);
    const [userPlan, setUserPlan] = useState('starter');
    const [dailyUsage, setDailyUsage] = useState(0);
    const [dailyLimit, setDailyLimit] = useState(FREE_DAILY_LIMIT);
    const [monthlyUsage, setMonthlyUsage] = useState(0);
    const [monthlyLimit, setMonthlyLimit] = useState(FREE_MONTHLY_LIMIT);
    
    // ✅ EDIT MODE STATE - Refactored to useReducer
    const [editState, dispatchEdit] = useReducer(editReducer, editInitialState);

    // Refs
    const modelMenuRef = useRef(null);
    const logoInputRef = useRef(null);
    const videoRef = useRef(null);

    // ✅ FETCH USER DATA - Credits + Usage Stats
    const fetchUserData = useCallback(async () => {
        try {
            // Fetch credits
            const creditsData = await apiService.getUserCredits();
            
            setUserCredits(creditsData.credits || 0);
            setUserPlan(creditsData.plan || 'starter');
            
            // Fetch video usage stats
            try {
                const usageStats = await apiService.getVideoUsageStats();
                
                setDailyUsage(usageStats.daily?.used || 0);
                setDailyLimit(usageStats.daily?.limit || FREE_DAILY_LIMIT);
                setMonthlyUsage(usageStats.monthly?.used || 0);
                setMonthlyLimit(usageStats.monthly?.limit || FREE_MONTHLY_LIMIT);
                
            } catch (usageError) {
                console.warn('Could not fetch usage stats:', usageError);
            }
            
        } catch (error) {
            console.error('Failed to load user data:', error);
            if (showNotification) {
                showNotification('Error: Could not load your account data.');
            }
        }
    }, [showNotification]);

    useEffect(() => {
        if (isOpen) {
            fetchUserData();
        }
    }, [isOpen, fetchUserData]);

    // ✅ HANDLE MODEL SELECTION
    const handleModelSelect = (model) => {
        if (model.isFree) {
            if (dailyUsage >= dailyLimit) {
                if (showNotification) {
                    showNotification('Daily free video limit reached. Please upgrade or wait until tomorrow.');
                }
                return;
            }
        } else {
            // Extract numeric cost from string like "18 credits" or "15 credits"
            const costMatch = model.cost.match(/\d+/);
            const cost = costMatch ? parseInt(costMatch[0]) : 0;
            
            if (userCredits < cost) {
                if (showNotification) {
                    showNotification(`Insufficient credits. You have ${userCredits.toFixed(1)} but need ${cost} credits.`);
                }
                return;
            }
        }
        
        setSelectedModel(model);
        setIsModelMenuOpen(false);
    };

    // ✅ GENERATE VIDEO - Fixed version with proper API calls and better error handling
    const handleGenerateVideo = async () => {
        if (!prompt.trim()) {
            if (showNotification) {
                showNotification('Please enter a prompt');
            }
            return;
        }

        // Check limits for free model
        if (selectedModel.isFree) {
            if (dailyUsage >= dailyLimit) {
                if (showNotification) {
                    showNotification('Daily free video limit reached. Please upgrade or use a paid model.');
                }
                return;
            }
        } else {
            // Extract numeric cost from string like "18 credits" or "15 credits"
            const costMatch = selectedModel.cost.match(/\d+/);
            const cost = costMatch ? parseInt(costMatch[0]) : 0;
            
            if (userCredits < cost) {
                if (showNotification) {
                    showNotification(`Insufficient credits. You have ${userCredits.toFixed(1)} credits but need ${cost} credits.`);
                }
                return;
            }
        }

        setIsGenerating(true);
        if (showNotification) {
            showNotification('Generating video...');
        }

        try {
            // ✅ Call the generateVideo API with correct parameters
            const result = await apiService.generateVideo({
                prompt: prompt.trim(),
                model: selectedModel.id,
                duration: duration || 5,
                aspectRatio: aspectRatio
            });

            if (result && (result.success || result.videoUrl || result.video_url || result.url)) {
                const url = result.videoUrl || result.video_url || result.url;
                setGeneratedVideoUrl(url);
                
                // Add to recent generations
                setRecentGenerations(prev => [{
                    url: url,
                    prompt: prompt,
                    timestamp: new Date().toISOString()
                }, ...prev.slice(0, 4)]);
                
                // ✅ Update credits and usage from response
                if (typeof result.creditsRemaining === 'number') {
                    setUserCredits(result.creditsRemaining);
                }
                
                if (result.allowances) {
                    const daily = result.allowances.daily || {};
                    const monthly = result.allowances.monthly || {};
                    
                    setDailyUsage(daily.used || 0);
                    setDailyLimit(daily.limit || FREE_DAILY_LIMIT);
                    setMonthlyUsage(monthly.used || 0);
                    setMonthlyLimit(monthly.limit || FREE_MONTHLY_LIMIT);
                }
                
                if (showNotification) {
                    showNotification('✅ Video generated successfully!');
                }
            } else {
                throw new Error('No video URL in response');
            }

        } catch (error) {
            console.error('❌ Error generating video:', error);
            
            // Better error messages based on error type
            let errorMessage = 'Failed to generate video';
            
            if (error.response?.status === 402) {
                const errorData = error.response.data;
                if (errorData.blocked_reason === 'daily_limit') {
                    errorMessage = 'Daily free video limit reached. Try again tomorrow or use credits.';
                } else if (errorData.blocked_reason === 'monthly_limit') {
                    errorMessage = 'Monthly free video limit reached. Upgrade your plan or use credits.';
                } else {
                    errorMessage = 'Insufficient credits. Please add more credits to continue.';
                }
            } else if (error.response?.status === 429) {
                errorMessage = 'Rate limit exceeded. Please wait a moment and try again.';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            if (showNotification) {
                showNotification(`Error: ${errorMessage}`);
            }
        } finally {
            setIsGenerating(false);
        }
    };

    // ✅ LOGO UPLOAD HANDLER
    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (!file.type.startsWith('image/')) {
                if (showNotification) {
                    showNotification('Please upload an image file');
                }
                return;
            }
            
            const url = URL.createObjectURL(file);
            dispatchEdit({
                type: 'SET_LOGO',
                payload: { file: file, url: url }
            });
            if (showNotification) {
                showNotification('Logo uploaded successfully');
            }
        }
    };

    // ✅ APPLY TEXT/LOGO OVERLAY TO VIDEO
    const applyOverlayToVideo = async () => {
        if (!generatedVideoUrl) {
            if (showNotification) {
                showNotification('Please generate a video first');
            }
            return;
        }

        if (!editState.overlayText.trim() && !editState.logoFile) {
            if (showNotification) {
                showNotification('Please add text or upload a logo');
            }
            return;
        }

        // This would typically call a backend video processing service
        // For now, we'll just show that the overlays are configured
        if (showNotification) {
            showNotification('Overlay settings saved! (Video processing would happen on backend)');
        }
        dispatchEdit({ type: 'SET_EDIT_MODE', payload: false });
    };

    // ✅ DOWNLOAD VIDEO
    const handleDownload = () => {
        if (!generatedVideoUrl) {
            if (showNotification) {
                showNotification('No video to download');
            }
            return;
        }

        try {
            const link = document.createElement('a');
            link.href = generatedVideoUrl;
            link.download = `video-${Date.now()}.mp4`;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            if (showNotification) {
                showNotification('Video download started');
            }
        } catch (error) {
            if (showNotification) {
                showNotification('Error downloading video');
            }
            console.error(error);
        }
    };

    // Calculate remaining free generations
    const freeGenerationsLeft = selectedModel.isFree 
        ? Math.max(0, dailyLimit - dailyUsage)
        : 0;

    return (
        <div className="vault-overlay" onClick={onClose}>
            <div
                className="vault-modal vault-modal--wide"
                onClick={(e) => e.stopPropagation()}
                style={{ maxWidth: 1180 }}
            >

                {/* Header */}
                <div className="vault-modal-head">
                    <Sparkles style={{ width: 22, height: 22, color: 'var(--accent)' }} />
                    <div>
                        <h2 className="vault-modal-title">Video Generator</h2>
                        <div className="vault-modal-sub">Create stunning videos with AI</div>
                    </div>
                    <div className="spacer" />

                    {/* User Stats */}
                    <div style={{ textAlign: 'right' }}>
                        <div className="vault-modal-sub" style={{ textTransform: 'none', letterSpacing: '0.08em' }}>
                            Daily {dailyUsage}/{dailyLimit} · Monthly {monthlyUsage}/{monthlyLimit}
                        </div>
                        <div className="vault-credit" style={{ marginTop: 4 }}>
                            <span className="dot" />
                            {userCredits.toFixed(1)} credits
                        </div>
                    </div>
                    <button onClick={onClose} className="vault-icon-btn" aria-label="Close">
                        <X />
                    </button>
                </div>

                {/* Main Content */}
                <div className="vault-modal-body" style={{ display: 'flex', gap: 0, padding: 0 }}>
                    {/* Left Panel - Model & Settings */}
                    <div
                        style={{
                            flex: '0 0 34%',
                            padding: 24,
                            borderRight: '1px solid var(--rule)',
                            overflowY: 'auto'
                        }}
                    >
                        {/* Model Selector */}
                        <div style={{ marginBottom: 24, position: 'relative' }} ref={modelMenuRef}>
                            <label className="vault-label">AI Model</label>
                            <button
                                onClick={() => setIsModelMenuOpen(!isModelMenuOpen)}
                                className="vault-select-input"
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 12,
                                    cursor: 'pointer'
                                }}
                            >
                                <span style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                                    <span style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {selectedModel.name}
                                    </span>
                                    <span className={`vault-badge ${selectedModel.isFree ? 'vault-badge--free' : 'vault-badge--premium'}`}>
                                        {selectedModel.cost}
                                    </span>
                                </span>
                                <ChevronDown
                                    style={{
                                        width: 16,
                                        height: 16,
                                        flex: 'none',
                                        transition: 'transform 160ms',
                                        transform: isModelMenuOpen ? 'rotate(180deg)' : 'none'
                                    }}
                                />
                            </button>

                            {isModelMenuOpen && (
                                <div
                                    className="vault-menu"
                                    style={{
                                        position: 'absolute',
                                        zIndex: 10,
                                        width: '100%',
                                        marginTop: 6,
                                        maxHeight: 280,
                                        overflowY: 'auto'
                                    }}
                                >
                                    {videoModels.map((model) => {
                                        const costMatch = model.cost.match(/\d+/);
                                        const cost = costMatch ? parseInt(costMatch[0]) : 0;
                                        const canAfford = model.isFree || userCredits >= cost;

                                        return (
                                            <button
                                                key={`${model.id}-${model.name}`}
                                                onClick={() => canAfford && handleModelSelect(model)}
                                                disabled={!canAfford}
                                                className={`vault-card ${selectedModel.id === model.id && selectedModel.name === model.name ? 'is-selected' : ''}`}
                                                style={{ marginBottom: 6, flexDirection: 'column', alignItems: 'flex-start', gap: 4 }}
                                            >
                                                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
                                                    <span className="title">{model.name}</span>
                                                    <span className={`vault-badge ${model.isFree ? 'vault-badge--free' : 'vault-badge--premium'}`}>
                                                        {model.cost}
                                                    </span>
                                                </span>
                                                <span className="desc">{model.description}</span>
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Prompt Input */}
                        <div style={{ marginBottom: 24 }}>
                            <label className="vault-label">Prompt</label>
                            <textarea
                                value={prompt}
                                onChange={(e) => setPrompt(e.target.value)}
                                placeholder="a dog on the beach"
                                className="vault-textarea"
                                rows="4"
                            />
                        </div>

                        {/* Aspect Ratio & Duration */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
                            <div>
                                <label className="vault-label">Aspect Ratio</label>
                                <select
                                    value={aspectRatio}
                                    onChange={(e) => setAspectRatio(e.target.value)}
                                    className="vault-select-input"
                                >
                                    <option value="16:9">16:9 (Landscape)</option>
                                    <option value="9:16">9:16 (Mobile)</option>
                                    <option value="1:1">1:1 (Square)</option>
                                </select>
                            </div>
                            <div>
                                <label className="vault-label">Duration</label>
                                <select
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    className="vault-select-input"
                                >
                                    <option value="5s">5s</option>
                                    <option value="10s">10s</option>
                                </select>
                            </div>
                        </div>

                        {/* Generate Button */}
                        <button
                            onClick={handleGenerateVideo}
                            disabled={isGenerating || !prompt.trim()}
                            className="vault-btn vault-btn--primary"
                            style={{ width: '100%', height: 44 }}
                        >
                            {isGenerating ? (
                                <>
                                    <span
                                        className="animate-spin"
                                        style={{
                                            width: 16,
                                            height: 16,
                                            border: '2px solid color-mix(in oklch, var(--accent-ink) 30%, transparent)',
                                            borderTopColor: 'var(--accent-ink)',
                                            borderRadius: '50%',
                                            display: 'inline-block'
                                        }}
                                    />
                                    Generating...
                                </>
                            ) : (
                                <>
                                    <Sparkles />
                                    {selectedModel.isFree
                                        ? `Generate (${freeGenerationsLeft} left today)`
                                        : `Generate (${selectedModel.cost})`
                                    }
                                </>
                            )}
                        </button>

                        {/* Free Usage Info */}
                        <div
                            style={{
                                marginTop: 16,
                                padding: 14,
                                background: 'var(--bg-1)',
                                border: '1px solid var(--rule)',
                                borderRadius: 2
                            }}
                        >
                            <div className="vault-label" style={{ marginBottom: 8 }}>Free Usage</div>
                            <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--fg-dim)', lineHeight: 1.7 }}>
                                Daily: {dailyUsage}/{dailyLimit} generations<br />
                                Monthly: {monthlyUsage}/{monthlyLimit} generations
                            </div>
                        </div>

                        {/* Recent Generations */}
                        {recentGenerations.length > 0 && (
                            <div style={{ marginTop: 24 }}>
                                <label className="vault-label">Recent Generations</label>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                    {recentGenerations.map((gen, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setGeneratedVideoUrl(gen.url)}
                                            style={{
                                                aspectRatio: '16 / 9',
                                                background: 'var(--bg-1)',
                                                border: '1px solid var(--rule)',
                                                borderRadius: 2,
                                                overflow: 'hidden',
                                                padding: 0,
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <video
                                                src={gen.url}
                                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                muted
                                            />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Center Panel - Preview - dynamic aspect ratio */}
                    <div style={{ flex: '1 1 33%', padding: 24, display: 'flex', flexDirection: 'column' }}>
                        <label className="vault-label">Preview</label>

                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                            <div
                                style={{
                                    background: 'var(--bg-1)',
                                    border: '1px solid var(--rule)',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    position: 'relative',
                                    ...(aspectRatio === '9:16'
                                        ? { width: '31%', paddingBottom: '55%' }
                                        : aspectRatio === '1:1'
                                        ? { width: '70%', paddingBottom: '70%' }
                                        : { width: '100%', paddingBottom: '56.25%' })
                                }}
                            >
                                <div className="absolute inset-0">
                                    {generatedVideoUrl ? (
                                        <div className="relative w-full h-full">
                                            <video
                                                ref={videoRef}
                                                src={generatedVideoUrl}
                                                controls
                                                className="w-full h-full object-contain"
                                            />
                                            
                                            {/* Text Overlay Preview - FIXED: Using editState */}
                                            {editState.overlayText && (
                                                <div 
                                                    className={`absolute ${positionClasses[editState.textPosition]} pointer-events-none`}
                                                    style={{
                                                        color: editState.textColor,
                                                        fontSize: `${editState.fontSize}px`,
                                                        fontFamily: editState.fontFamily,
                                                        fontWeight: editState.isBold ? 'bold' : 'normal',
                                                        fontStyle: editState.isItalic ? 'italic' : 'normal',
                                                        textShadow: `2px 2px 4px ${editState.shadowColor}${Math.round(editState.shadowOpacity * 255).toString(16).padStart(2, '0')}`
                                                    }}
                                                >
                                                    {editState.overlayText}
                                                </div>
                                            )}
                                            
                                            {/* Logo Overlay Preview - FIXED: Using editState */}
                                            {editState.logoUrl && (
                                                <img
                                                    src={editState.logoUrl}
                                                    alt="Logo"
                                                    className={`absolute ${positionClasses[editState.logoPosition]} pointer-events-none`}
                                                    style={{
                                                        width: `${editState.logoSize}%`,
                                                        opacity: editState.logoOpacity
                                                    }}
                                                />
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--fg-faint)' }}>
                                            <div style={{ textAlign: 'center' }}>
                                                <Play style={{ width: 44, height: 44, margin: '0 auto 8px', opacity: 0.5 }} />
                                                <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.08em' }}>Your video will appear here</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Video Controls */}
                        {generatedVideoUrl && (
                            <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                                <button
                                    onClick={handleDownload}
                                    className="vault-btn vault-btn--primary"
                                    style={{ flex: 1 }}
                                >
                                    <Download />
                                    Download
                                </button>
                                <button
                                    onClick={() => dispatchEdit({ type: 'SET_EDIT_MODE', payload: !editState.isEditMode })}
                                    className={`vault-btn ${editState.isEditMode ? 'is-selected' : ''}`}
                                    style={{ flex: 1 }}
                                >
                                    <Edit2 />
                                    Edit
                                </button>
                                <button className="vault-icon-btn" style={{ border: '1px solid var(--rule)' }} aria-label="Share">
                                    <Share2 />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Right Panel - Edit Controls */}
                    <div style={{ flex: '1 1 33%', padding: 24, borderLeft: '1px solid var(--rule)', overflowY: 'auto' }}>
                        {editState.isEditMode ? (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                                    <label className="vault-label" style={{ marginBottom: 0 }}>Advanced Edit</label>
                                    <button
                                        onClick={() => dispatchEdit({ type: 'SET_EDIT_MODE', payload: false })}
                                        className="vault-btn vault-btn--ghost"
                                        style={{ height: 28, padding: '0 10px', fontSize: 10 }}
                                    >
                                        Close
                                    </button>
                                </div>

                                {/* Text Overlay Section */}
                                <div style={{ background: 'var(--bg-1)', border: '1px solid var(--rule)', padding: 16, borderRadius: 2, marginBottom: 16 }}>
                                    <div className="vault-label">
                                        <Type style={{ width: 14, height: 14 }} />
                                        Text Overlay
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <div>
                                            <label className="vault-modal-sub" style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}>Text</label>
                                            <input
                                                type="text"
                                                value={editState.overlayText}
                                                onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'overlayText', payload: e.target.value })}
                                                placeholder="Enter text..."
                                                className="vault-field"
                                            />
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <EditSlider
                                                label="Font Size"
                                                value={editState.fontSize}
                                                unit="px"
                                                onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'fontSize', payload: parseInt(e.target.value) })}
                                                min="12"
                                                max="72"
                                            />
                                            <div>
                                                 <label className="vault-modal-sub" style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}>Text Color</label>
                                                 <input
                                                     type="color"
                                                     value={editState.textColor}
                                                     onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'textColor', payload: e.target.value })}
                                                     style={{ width: '100%', height: 40, background: 'var(--bg-1)', border: '1px solid var(--rule)', borderRadius: 2, cursor: 'pointer' }}
                                                 />
                                             </div>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                                            <EditSlider
                                                label="Shadow"
                                                value={Math.round(editState.shadowOpacity * 100)}
                                                unit="%"
                                                onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'shadowOpacity', payload: parseFloat(e.target.value) / 100 })}
                                                min="0"
                                                max="100"
                                            />
                                             <div>
                                                 <label className="vault-modal-sub" style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}>Shadow Color</label>
                                                 <input
                                                     type="color"
                                                     value={editState.shadowColor}
                                                     onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'shadowColor', payload: e.target.value })}
                                                     style={{ width: '100%', height: 40, background: 'var(--bg-1)', border: '1px solid var(--rule)', borderRadius: 2, cursor: 'pointer' }}
                                                 />
                                             </div>
                                        </div>

                                        <div>
                                            <label className="vault-modal-sub" style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}>Font Family</label>
                                            <select
                                                value={editState.fontFamily}
                                                onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'fontFamily', payload: e.target.value })}
                                                className="vault-select-input"
                                            >
                                                <option value="Arial">Arial</option>
                                                <option value="Helvetica">Helvetica</option>
                                                <option value="Times New Roman">Times New Roman</option>
                                                <option value="Courier New">Courier New</option>
                                                <option value="Georgia">Georgia</option>
                                                <option value="Verdana">Verdana</option>
                                            </select>
                                        </div>

                                        <div style={{ display: 'flex', gap: 8 }}>
                                            <button
                                                onClick={() => dispatchEdit({ type: 'SET_FIELD', field: 'isBold', payload: !editState.isBold })}
                                                className={`vault-btn ${editState.isBold ? 'vault-btn--primary' : ''}`}
                                                style={{ flex: 1, fontWeight: 'bold' }}
                                            >
                                                B
                                            </button>
                                            <button
                                                onClick={() => dispatchEdit({ type: 'SET_FIELD', field: 'isItalic', payload: !editState.isItalic })}
                                                className={`vault-btn ${editState.isItalic ? 'vault-btn--primary' : ''}`}
                                                style={{ flex: 1, fontStyle: 'italic' }}
                                            >
                                                I
                                            </button>
                                        </div>

                                        <div>
                                            <label className="vault-modal-sub" style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}>Position</label>
                                            <select
                                                value={editState.textPosition}
                                                onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'textPosition', payload: e.target.value })}
                                                className="vault-select-input"
                                            >
                                                {Object.keys(positionClasses).map(pos => (
                                                    <option key={pos} value={pos} className="capitalize">
                                                        {pos.replace(/-/g, ' ')}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Logo Overlay Section */}
                                <div style={{ background: 'var(--bg-1)', border: '1px solid var(--rule)', padding: 16, borderRadius: 2, marginBottom: 16 }}>
                                    <div className="vault-label">
                                        <ImageIcon style={{ width: 14, height: 14 }} />
                                        Logo Overlay
                                    </div>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                        <button
                                            onClick={() => logoInputRef.current?.click()}
                                            className="vault-btn"
                                            style={{ width: '100%' }}
                                        >
                                            <UploadCloud />
                                            {editState.logoUrl ? 'Change Logo' : 'Upload Logo'}
                                        </button>
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            className="hidden"
                                        />

                                        {editState.logoUrl && (
                                            <>
                                                <EditSlider
                                                    label="Logo Size"
                                                    value={editState.logoSize}
                                                    unit="%"
                                                    onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'logoSize', payload: parseInt(e.target.value) })}
                                                    min="5"
                                                    max="50"
                                                />
                                                <EditSlider
                                                    label="Logo Opacity"
                                                    value={Math.round(editState.logoOpacity * 100)}
                                                    unit="%"
                                                    onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'logoOpacity', payload: parseFloat(e.target.value) / 100 })}
                                                    min="0"
                                                    max="100"
                                                />

                                                <div>
                                                    <label className="vault-modal-sub" style={{ display: 'block', marginBottom: 6, textTransform: 'none', letterSpacing: '0.08em' }}>Position</label>
                                                    <select
                                                        value={editState.logoPosition}
                                                        onChange={(e) => dispatchEdit({ type: 'SET_FIELD', field: 'logoPosition', payload: e.target.value })}
                                                        className="vault-select-input"
                                                    >
                                                        {Object.keys(positionClasses).map(pos => ( 
                                                            <option key={pos} value={pos} className="capitalize">
                                                                {pos.replace(/-/g, ' ')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>

                                                <button
                                                    onClick={() => dispatchEdit({ type: 'REMOVE_LOGO' })}
                                                    className="vault-btn vault-btn--danger"
                                                    style={{ width: '100%' }}
                                                >
                                                    Remove Logo
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Apply Button */}
                                <button
                                    onClick={applyOverlayToVideo}
                                    className="vault-btn vault-btn--primary"
                                    style={{ width: '100%', height: 44 }}
                                >
                                    <Save />
                                    Apply Overlays
                                </button>

                                <p style={{ fontFamily: 'var(--mono)', fontSize: 10, letterSpacing: '0.06em', color: 'var(--fg-faint)', textAlign: 'center', marginTop: 8 }}>
                                    * Overlay processing requires backend video editing service
                                </p>
                            </>
                        ) : (
                            <div style={{ textAlign: 'center', color: 'var(--fg-faint)', padding: '48px 0' }}>
                                <Edit2 style={{ width: 44, height: 44, margin: '0 auto 12px', opacity: 0.3 }} />
                                <p style={{ fontFamily: 'var(--mono)', fontSize: 12, letterSpacing: '0.08em' }}>Click "Edit" to add text overlays and logos</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default VideoGenModal;